import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AddressFormGroup } from '../../../../forms/address-form-group';
import { AddressFormFactory } from '../../../../form-factories/address-form-factory';
import { CANADA_COUNTRY_ID } from '../../../../consts/consts';
import { LoadingOptions } from '../../../../models/shared/loading-options';
import { MemberUser } from '../../../../models/users/dto/member-user';
import { ChangeAddressRequest } from '../../../../models/users/requests/submissionRequests/change-address-request';
import { MediaUtils } from '../../../../utils/media-utils';
import { AddressChangeSubmissionFormViewModel } from './address-change-submission-form-view-model';
import { maxFileSizeValidator } from '../../../../ui/form-components/file-uploader/file-upload-field/max-file-size-validator';

type AddressChangeForm = FormGroup<{
  address: AddressFormGroup;
  files: FormControl<File[]>;
}>;

@Component({
  selector: 'app-address-change-submission-form',
  templateUrl: './address-change-submission-form.component.html',
  styleUrls: ['./address-change-submission-form.component.scss'],
  providers: [AddressChangeSubmissionFormViewModel],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddressChangeSubmissionFormComponent implements OnChanges {
  @Input() user!: MemberUser;
  @Input() acceptedFileTypes: string[] = ['image/png', 'image/jpeg', 'application/pdf'];
  @Input() allowMultipleFiles: boolean = false;
  @Input() maxFileSizeInMb: number = 3;
  @Input() filesRequired: boolean = false;

  @Output() loadingOpts = new EventEmitter<LoadingOptions>();

  private viewModel = inject(AddressChangeSubmissionFormViewModel);
  private cdr = inject(ChangeDetectorRef);

  public formGroup: AddressChangeForm = this.buildForm();
  public formSubmitted = false;

  public readonly customErrorMessages = {
    files: {
      required: $localize`A file upload is required`,
      maxFileSize: $localize`File size exceeds the maximum limit of ${this.maxFileSizeInMb}MB`
    }
  };

  constructor() {
    this.viewModel.loadingOpts$.pipe(takeUntilDestroyed()).subscribe(opts => this.loadingOpts.emit(opts));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.user && this.user) {
      this.resetFormFromUser();
      this.cdr.markForCheck();
    }
    if (changes.filesRequired || changes.maxFileSizeInMb) {
      this.applyFileValidators();
    }
  }

  public submitForm(): void {
    this.formSubmitted = true;
    this.formGroup.markAllAsTouched();
    this.cdr.markForCheck();
    if (this.formGroup.invalid) return;

    const request = this.buildRequest();
    const files = this.formGroup.controls.files.value;
    if (this.filesRequired && files.length) {
      Promise.all(files.map(file => MediaUtils.fileToUserFile(file))).then(userFiles => {
        this.viewModel.submitRequest(request, userFiles);
      });
    } else {
      this.viewModel.submitRequest(request);
    }
  }

  private buildForm(user?: MemberUser): AddressChangeForm {
    const address = user?.address;
    const addressForm = AddressFormFactory.createAddressForm(address);

    if (address) {
      // The member endpoints populate the nested state/country objects, not always the raw ids.
      addressForm.controls.stateId.setValue(address.stateId ?? address.state?.id ?? null, { emitEvent: false });
      addressForm.controls.countryId.setValue(address.countryId ?? address.country?.id ?? CANADA_COUNTRY_ID, {
        emitEvent: false
      });
    }

    return new FormGroup({
      address: addressForm,
      files: new FormControl<File[]>({ value: [], disabled: true }, { nonNullable: true })
    });
  }

  private resetFormFromUser(): void {
    this.formGroup = this.buildForm(this.user);
    this.applyFileValidators();
    this.formSubmitted = false;
  }

  private applyFileValidators(): void {
    const filesControl = this.formGroup.controls.files;
    if (this.filesRequired) {
      filesControl.setValidators([Validators.required, maxFileSizeValidator(this.maxFileSizeInMb)]);
      filesControl.enable({ emitEvent: false });
    } else {
      filesControl.clearValidators();
      filesControl.setValue([], { emitEvent: false });
      filesControl.disable({ emitEvent: false });
    }
    filesControl.updateValueAndValidity({ emitEvent: false });
  }

  private buildRequest(): ChangeAddressRequest {
    const address = this.formGroup.controls.address.getRawValue();
    const request = new ChangeAddressRequest(this.user);
    request.addressLine1 = address.addressLine1 ?? '';
    request.addressLine2 = address.addressLine2 ?? '';
    request.addressLine3 = address.addressLine3 ?? '';
    request.city = address.city ?? '';
    request.stateId = address.stateId ?? 0;
    request.countryId = address.countryId ?? CANADA_COUNTRY_ID;
    request.postalCode = address.postalCode ?? '';
    return request;
  }
}
